import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { ButtonTypeEnum } from '../../../enums/button-type-enum';
import { SnackbarSeverityEnum } from '../../../enums/snackbar-severity-enum';
import { handle400Error } from '../../../errors/400/errorHandler';
import { useInviteTokenValidation } from '../../../hooks/company/registration/useInviteTokenValidation';
import useSnackbar from '../../../hooks/useSnackBar';
import { IRegistrationRequest } from '../../../interfaces/auth.interface';
import { useRegistrationMutation } from '../../../store/apiSlice/auth/authApi';
import { useThemeContext } from '../../../theme/ThemeContextProvider';
import { errorHelper } from '../../../utils/helper/error-helper';
import { registrationSchema } from '../../../validation/auth-validation';
import RightArrowIcon from '../../Icons/RightArrowIcon';
import CustomButton from '../../MUIComponents/CustomButton';
import Input from '../../MUIComponents/Input';
import SnackbarCustom from '../../MUIComponents/SnackbarCustom';
import { Loading } from '../../shared/loader/Loader';
import PasswordValidation from '../Elements/PasswordValidation';

interface RegistrationFormInputs {
  email: string;
  password: string;
  confirmPassword: string;
}

const Registration = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const inviteEmail = searchParams.get('inviteEmail');
  const inviteToken = searchParams.get('inviteToken');

  const { theme } = useThemeContext();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));

  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const { isInviteTokenLoading } = useInviteTokenValidation({
    showError: showSnackbar,
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, dirtyFields },
  } = useForm<RegistrationFormInputs>({
    criteriaMode: 'all',
    defaultValues: {
      email: inviteEmail ?? '',
    },
    resolver: yupResolver(registrationSchema, { abortEarly: false }),
    mode: 'onChange',
  });
  // eslint-disable-next-line
  const [registration, { isLoading, isError, error, data: responseData }] =
    useRegistrationMutation();

  const onSubmit = async (data: RegistrationFormInputs) => {
    try {
      const registrationData: IRegistrationRequest = {
        email: data.email,
        password: data.password,
      };

      if (!inviteEmail || !inviteToken) {
        throw new Error('Invite email or token is missing');
      }
      await registration({
        inviteToken,
        registrationData,
      }).unwrap();
      showSnackbar('Registration successful', SnackbarSeverityEnum.SUCCESS);
      navigate('/verify-code', {
        replace: true,
        state: { email: data.email || '', password: data.password || '' },
      });
    } catch (error: any) {
      const showError = (msg: string) =>
        showSnackbar(msg, SnackbarSeverityEnum.ERROR);

      if (error.status && error.status === 400) {
        return handle400Error({
          error,
          showError,
        });
      }
      showSnackbar(errorHelper(error), SnackbarSeverityEnum.ERROR);
      console.error('Registration failed:', error);
    }
  };

  if (isInviteTokenLoading) {
    return <Loading />;
  }

  return (
    <Box
      component={'form'}
      display="flex"
      flexDirection="column"
      gap="3rem"
      width={'min(26.5625rem,100%)'}
    >
      <Typography variant="h2">Create account</Typography>
      <Box display="flex" flexDirection="column" gap="2.25rem" width={'100%'}>
        <Input
          caption="Email:"
          type="email"
          variant="outlined"
          fullWidth
          placeholder="Enter email address"
          defaultValue={inviteEmail ?? ''}
          register={register('email')}
          error={!!errors.email}
          helperText={errors.email ? errors.email.message : ''}
          disabled
          sx={{
            ...emailFieldStyles,
          }}
        />
        <Input
          caption="New password:"
          type="password"
          variant="outlined"
          fullWidth
          placeholder="Enter new password"
          register={register('password')}
          error={!!errors.password}
        />
        <PasswordValidation
          isDirty={Object.keys(dirtyFields).length > 0}
          errorMessages={
            Object.values(errors?.password?.types ?? {})
              .filter(Boolean)
              .flat() as string[]
          }
        />
        <Input
          caption="Confirm new password:"
          type="password"
          variant="outlined"
          fullWidth
          placeholder="Confirm new password"
          register={register('confirmPassword', {
            validate: value =>
              value === watch('password') || 'Passwords do not match',
          })}
          error={!!errors.confirmPassword}
          helperText={
            errors.confirmPassword ? errors.confirmPassword.message : ''
          }
        />
      </Box>
      <CustomButton
        type={'submit'}
        buttonWidth={mobileView ? '100%' : '50%'}
        variantType={ButtonTypeEnum.PRIMARY}
        fullWidth
        onClick={handleSubmit(onSubmit)}
        disabled={isLoading}
        icon={<RightArrowIcon />}
      >
        Sign Up
      </CustomButton>

      <SnackbarCustom
        open={snackbar.open}
        onClose={closeSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
        autoHideDuration={null}
      />
    </Box>
  );
};

export default Registration;

const emailFieldStyles = {
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent',
  },
  '& .MuiOutlinedInput-input': {
    backgroundColor: 'transparent',

    '&:disabled': {
      opacity: 1,
    },

    '&::placeholder': {
      opacity: 1,
    },
  },
  input: {
    '&:-webkit-autofill': {
      WebkitBoxShadow: `none`,
    },
    '&::placeholder': {
      opacity: 1,
    },
  },
};
